import { useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, errText } from "../lib/api";
import { PROVINCES } from "../lib/constants";
import { CityInput } from "./CityInput";
import { LikeThisSignup } from "./LikeThisSignup";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const RADII = [10, 25, 50, 100, 0];
const TXT = {
  en: { email: "Your email", news: "Newsletter: pet tips, news and offers", alerts: "Lost-pet alerts near me", city: "Your city",
    prov: "Province", radius: "How far", whole: "Whole province", btn: "Sign me up", thanks: "Thanks! You're on the list.", pick: "Choose at least one" },
  fr: { email: "Votre courriel", news: "Infolettre : conseils, nouvelles et offres", alerts: "Alertes d'animaux perdus près de chez moi", city: "Votre ville",
    prov: "Province", radius: "Distance", whole: "Toute la province", btn: "Je m'inscris", thanks: "Merci ! Vous êtes inscrit(e).", pick: "Choisissez au moins une option" },
};

const PROV_FR = { BC: "Colombie-Britannique", NS: "Nouvelle-Écosse", NB: "Nouveau-Brunswick", NL: "Terre-Neuve-et-Labrador", PE: "Île-du-Prince-Édouard", QC: "Québec", NT: "Territoires du Nord-Ouest" };

const Opt = ({ id, checked, onChange, label }) => (
  <label className="flex items-center gap-2.5 text-sm text-[#12234A] cursor-pointer">
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} data-testid={id} className="data-[state=checked]:bg-[#F5551E] data-[state=checked]:border-[#F5551E]" />
    {label}
  </label>
);

const AlertArea = ({ f, set, t, lang }) => (
  <div className="grid grid-cols-2 gap-2" data-testid="popup-signup-area">
    <div className="col-span-2">
      <CityInput value={f.city} province={f.province} onChange={(v) => set("city", v)} onPick={(c) => { set("city", c.city); set("province", c.province); }}
        placeholder={t.city} testid="popup-signup-city" className="h-10 rounded-xl" />
    </div>
    <Select value={f.province} onValueChange={(v) => set("province", v)}>
      <SelectTrigger className="h-10 rounded-xl" data-testid="popup-signup-province"><SelectValue placeholder={t.prov} /></SelectTrigger>
      <SelectContent className="bg-white">{PROVINCES.filter((p) => p.code !== "all").map((p) => <SelectItem key={p.code} value={p.code}>{(lang === "fr" && PROV_FR[p.code]) || p.name}</SelectItem>)}</SelectContent>
    </Select>
    <Select value={String(f.radius_km)} onValueChange={(v) => set("radius_km", Number(v))}>
      <SelectTrigger className="h-10 rounded-xl" data-testid="popup-signup-radius"><SelectValue placeholder={t.radius} /></SelectTrigger>
      <SelectContent className="bg-white">{RADII.map((r) => <SelectItem key={r} value={String(r)}>{r ? `${r} km` : t.whole}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export function PopupSignup({ p, lang, preview, onDone }) {
  if (p._page === "listing") return <LikeThisSignup p={p} lang={lang} preview={preview} onDone={onDone} />;
  return <ListSignup p={p} lang={lang} preview={preview} onDone={onDone} />;
}

function ListSignup({ p, lang, preview, onDone }) {
  const t = TXT[lang] || TXT.en;
  // French visitors fall back to the built-in French wording, not the admin's English text
  const pick = (k) => (lang === "fr" ? p[`${k}_fr`] : p[k]);
  const [f, setF] = useState({ email: "", newsletter: !!p.signup_newsletter, alerts: !p.signup_newsletter && !!p.signup_alerts, city: "", province: "ON", radius_km: 25, website: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setErr(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (preview) return;
    if (!f.newsletter && !f.alerts) return setErr(t.pick);
    setBusy(true); setErr("");
    try { await api.post("/subscribe", { ...f, lang, ab_id: p._ab || "", variant: p._v || "", page: p._page || "home" }); setDone(true); onDone?.(); }
    catch (e2) { setErr(errText(e2)); }
    finally { setBusy(false); }
  };

  if (done) {
    return <p className="mt-5 flex items-center gap-2 text-emerald-700 font-semibold" data-testid="popup-signup-thanks"><CheckCircle2 size={20} /> {pick("signup_thanks") || t.thanks}</p>;
  }
  return (
    <form onSubmit={submit} className="mt-5 space-y-3" data-testid="popup-signup-form">
      <Input type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} placeholder={t.email} className="h-11 rounded-xl" data-testid="popup-signup-email" />
      <input tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />
      {p.signup_newsletter && <Opt id="popup-signup-newsletter" checked={f.newsletter} onChange={(v) => set("newsletter", v)} label={t.news} />}
      {p.signup_alerts && <Opt id="popup-signup-alerts" checked={f.alerts} onChange={(v) => set("alerts", v)} label={t.alerts} />}
      {p.signup_alerts && f.alerts && <AlertArea f={f} set={set} t={t} lang={lang} />}
      {err && <p className="text-sm text-red-600" data-testid="popup-signup-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="popup-signup-submit" className="w-full h-11 rounded-full bg-[#12234A] hover:bg-[#1C3166] text-white font-semibold transition-colors flex items-center justify-center">
        {busy ? <Loader2 size={18} className="animate-spin" /> : pick("signup_button_text") || t.btn}
      </button>
    </form>
  );
}
